import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { toast } from "sonner";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link2, ImagePlus, Quote, Undo2, Redo2, Minus } from "lucide-react";
import { api, errMsg } from "../lib/api";

const AdaImage = Image.extend({
  addAttributes() {
    return { ...this.parent?.(), class: { default: "img-full img-center" } };
  },
});

const SIZES = [["img-s", "Küçük"], ["img-m", "Orta"], ["img-l", "Büyük"], ["img-full", "Tam"]];
const ALIGNS = [["img-left", "Sola"], ["img-center", "Ortaya"], ["img-right", "Sağa"]];

export const uploadFile = async (file, lessonId) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("lesson_id", lessonId || "");
  const { data } = await api.post("/admin/upload", fd);
  return data;
};

const Btn = ({ on, active, label, children, testid }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} title={label} aria-label={label} data-testid={testid}
    className={`h-9 min-w-9 px-2 flex items-center justify-center text-xs font-semibold border ${active ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-200 bg-white hover:bg-zinc-100"}`}>{children}</button>
);

export default function RichEditor({ value, onChange, lessonId }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3, 4] }, link: { openOnClick: false, autolink: true } }), AdaImage],
    content: value || "",
    shouldRerenderOnTransaction: true,
    editorProps: { attributes: { class: "lesson-content min-h-[320px] p-4 outline-none", "data-testid": "rich-editor" } },
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });
  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const imgClass = editor.getAttributes("image").class || "";
  const setImg = (group, cls) => {
    const rest = imgClass.split(" ").filter((x) => x && !group.some(([g]) => g === x));
    c().updateAttributes("image", { class: [...rest, cls].join(" ") }).run();
  };
  const addLink = () => {
    const url = window.prompt("Bağlantı adresi (https://...)", editor.getAttributes("link").href || "https://");
    if (url === null) return;
    if (!url) return c().unsetLink().run();
    if (!/^https?:\/\//i.test(url)) return toast.error("Bağlantı https:// ile başlamalı");
    c().extendMarkRange("link").setLink({ href: url }).run();
  };
  const pickImage = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    try {
      const up = await uploadFile(file, lessonId);
      const caption = window.prompt("Görsel açıklaması (isteğe bağlı)") || "";
      const nodes = [{ type: "image", attrs: { src: up.url, alt: caption || file.name, title: caption } }];
      if (caption) nodes.push({ type: "paragraph", content: [{ type: "text", text: caption, marks: [{ type: "italic" }] }] });
      c().insertContent(nodes).run();
      toast.success("Görsel yüklendi");
    } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <div className="border border-zinc-300 bg-white">
      <div className="flex flex-wrap gap-1 p-2 border-b border-zinc-200 bg-zinc-50 sticky top-0 z-10" data-testid="editor-toolbar">
        <Btn on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Başlık" testid="ed-h2"><Heading2 size={16} /></Btn>
        <Btn on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Alt başlık" testid="ed-h3"><Heading3 size={16} /></Btn>
        <Btn on={() => c().toggleBold().run()} active={editor.isActive("bold")} label="Kalın" testid="ed-bold"><Bold size={16} /></Btn>
        <Btn on={() => c().toggleItalic().run()} active={editor.isActive("italic")} label="İtalik" testid="ed-italic"><Italic size={16} /></Btn>
        <Btn on={() => c().toggleUnderline().run()} active={editor.isActive("underline")} label="Altı çizili"><Underline size={16} /></Btn>
        <Btn on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Madde işaretli liste" testid="ed-ul"><List size={16} /></Btn>
        <Btn on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Numaralı liste" testid="ed-ol"><ListOrdered size={16} /></Btn>
        <Btn on={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Önemli bilgi kutusu"><Quote size={16} /></Btn>
        <Btn on={() => c().setHorizontalRule().run()} label="Ayırıcı çizgi"><Minus size={16} /></Btn>
        <Btn on={addLink} active={editor.isActive("link")} label="Bağlantı" testid="ed-link"><Link2 size={16} /></Btn>
        <Btn on={() => fileRef.current.click()} label="Bilgisayardan görsel ekle" testid="ed-image"><ImagePlus size={16} /><span className="ml-1">Görsel</span></Btn>
        <Btn on={() => c().undo().run()} label="Geri al"><Undo2 size={16} /></Btn>
        <Btn on={() => c().redo().run()} label="Yinele"><Redo2 size={16} /></Btn>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickImage} data-testid="ed-image-input" />
      </div>
      {editor.isActive("image") && (
        <div className="flex flex-wrap gap-1 p-2 border-b border-zinc-200 bg-red-50 text-xs items-center" data-testid="image-toolbar">
          <span className="font-bold mr-1">Görsel:</span>
          {SIZES.map(([k, l]) => <Btn key={k} on={() => setImg(SIZES, k)} active={imgClass.includes(k)} label={l} testid={`ed-${k}`}>{l}</Btn>)}
          <span className="w-2" />
          {ALIGNS.map(([k, l]) => <Btn key={k} on={() => setImg(ALIGNS, k)} active={imgClass.includes(k)} label={l} testid={`ed-${k}`}>{l}</Btn>)}
          <span className="w-2" />
          <Btn on={() => { const t = window.prompt("Görsel açıklaması", editor.getAttributes("image").alt || ""); if (t !== null) c().updateAttributes("image", { alt: t, title: t }).run(); }} label="Açıklama">Açıklama</Btn>
          <Btn on={() => fileRef.current.click()} label="Değiştir (yeni görsel ekler, eskisini silin)">Değiştir</Btn>
          <Btn on={() => c().deleteSelection().run()} label="Görseli sil" testid="ed-image-delete">Sil</Btn>
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
